// screens/supplier/SupplierEditProduct.js
import React, { useState, useEffect } from "react";
import { Upload, X, Plus, Save, ArrowLeft, Video } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { uploadToCloudinary } from "../../utils/cloudinary";
import { useAuth } from "../../context/AuthContext";

// Helper: upload video to Cloudinary
const uploadVideoToCloudinary = async (file) => {
  if (!file) throw new Error("No video selected");
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);
  formData.append("resource_type", "video");
  const { data } = await axios.post(
    `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_NAME}/video/upload`,
    formData
  );
  return data;
};

const SupplierEditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: { en: "", hi: "", es: "", zh: "" },
    description: { en: "", hi: "", es: "", zh: "" },
    material: { en: "", hi: "", es: "", zh: "" },
    finishType: { en: "", hi: "", es: "", zh: "" },
    origin: { en: "Made in India", hi: "भारत में निर्मित", es: "Hecho en India", zh: "印度制造" },
    careInstruction: { en: "", hi: "", es: "", zh: "" },
    return_policy: {
      en: "7-day replacement for damages",
      hi: "क्षति के लिए 7 दिन की प्रतिस्थापन",
      es: "Reemplazo de 7 días por daños",
      zh: "损坏7天更换",
    },
    measurement: { en: "", hi: "", es: "", zh: "" },
    productType: { en: "", hi: "", es: "", zh: "" },
    sku: "",
    category: "",
    subCategory: "",
    height: "",
    weight: "",
    brand: "",
    price: "",
    previous_price: "",
    stock: "",
    stock_status: "active",
    gstPercentage: 18,
    shippingCharge: 150,
    rating: 4.8,
    features: [],
    isBestProduct: false,
  });

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [materials] = useState(["Brass", "Stone", "Marble", "Wood", "Bronze"]);
  const [mainImage, setMainImage] = useState(null);
  const [subImages, setSubImages] = useState([]);
  const [productVideo, setProductVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [featuresInput, setFeaturesInput] = useState({ en: "", hi: "", es: "", zh: "" });
  const [activeLanguage, setActiveLanguage] = useState("en");

  const getEnglishText = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (field.en) return field.en;
    return Object.values(field)[0] || "";
  };

  const ensureMultilingual = (value, defaultValue = "") => {
    if (!value) return { en: defaultValue, hi: defaultValue, es: defaultValue, zh: defaultValue };
    if (typeof value === "string") return { en: value, hi: value, es: value, zh: value };
    return {
      en: value.en || defaultValue,
      hi: value.hi || value.en || defaultValue,
      es: value.es || value.en || defaultValue,
      zh: value.zh || value.en || defaultValue,
    };
  };

  // Fetch product
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/products/get-product-by-id/${id}`
        );
        const product = res.data?.product;
        if (!product) throw new Error("Product not found");

        const supplierId = product.supplier?._id || product.supplier;
        if (supplierId !== user?._id) {
          toast.error("You don't have permission to edit this product");
          navigate("/seller/products");
          return;
        }

        setFormData({
          name: ensureMultilingual(product.name),
          description: ensureMultilingual(product.description),
          material: ensureMultilingual(product.material),
          finishType: ensureMultilingual(product.finishType),
          origin: ensureMultilingual(product.origin, "Made in India"),
          careInstruction: ensureMultilingual(product.careInstruction),
          return_policy: ensureMultilingual(product.return_policy, "7-day replacement for damages"),
          measurement: ensureMultilingual(product.measurement),
          productType: ensureMultilingual(product.productType, ""),
          sku: product.sku || "",
          category: product.category?._id || "",
          subCategory: product.subCategory?._id || "",
          height: product.height || "",
          weight: product.weight || "",
          brand: product.brand || "",
          price: product.price || "",
          previous_price: product.previous_price || "",
          stock: product.stock || 0,
          stock_status: product.stock_status || "active",
          gstPercentage: product.gstPercentage || 18,
          shippingCharge: product.shippingCharge || 150,
          rating: product.ratings || 4.8,
          features: Array.isArray(product.features)
            ? product.features.map(f => ensureMultilingual(f))
            : [],
          isBestProduct: product.isBestProduct || false,
        });

        if (product.images?.length > 0) {
          setMainImage(product.images[0]);
          setSubImages(product.images.slice(1));
        }

        // ── Load existing video if present ──────────────────────────
        if (product.productVideo) {
          setProductVideo(product.productVideo);
        }
      } catch (error) {
        console.error("Fetch error:", error);
        toast.error("Failed to load product");
        navigate("/seller/products");
      } finally {
        setFetchLoading(false);
      }
    };

    if (id && user?._id) fetchProduct();
  }, [id, user?._id, navigate]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/categories/get-all-categories`);
        setCategories(res.data || []);
      } catch { toast.error("Failed to load categories"); }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!formData.category) { setSubCategories([]); return; }
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/sub-categories/get-all-sub-categories`);
        setSubCategories(res.data.filter(sub => sub.category._id === formData.category));
      } catch { toast.error("Failed to load subcategories"); }
    };
    fetchSubCategories();
  }, [formData.category]);

  const LanguageTabs = () => (
    <div className="flex space-x-2 mb-4">
      {["en", "hi", "es", "zh"].map((lang) => (
        <button key={lang} type="button" onClick={() => setActiveLanguage(lang)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeLanguage === lang ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}>
          {lang === "en" ? "English" : lang === "hi" ? "हिंदी" : lang === "es" ? "Español" : "中文"}
        </button>
      ))}
    </div>
  );

  const handleMainImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const toastId = toast.loading("Uploading main image...");
    try {
      const upload = await uploadToCloudinary(file);
      setMainImage(upload.secure_url);
      toast.dismiss(toastId);
      toast.success("Main image uploaded!");
    } catch { toast.dismiss(toastId); toast.error("Main image upload failed!"); }
  };

  const handleSubImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const toastId = toast.loading("Uploading additional images...");
    try {
      const uploads = await Promise.all(files.map(uploadToCloudinary));
      setSubImages(prev => [...prev, ...uploads.map(u => u.secure_url)]);
      toast.dismiss(toastId);
      toast.success("Additional images uploaded!");
    } catch { toast.dismiss(toastId); toast.error("Some images failed!"); }
  };

  const removeSubImage = (index) => setSubImages(prev => prev.filter((_, i) => i !== index));

  // ── Video handler ────────────────────────────────────────────────
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please select a video file");
      return;
    }
    const toastId = toast.loading("Uploading video...");
    try {
      const upload = await uploadVideoToCloudinary(file);
      setProductVideo(upload.secure_url);
      toast.dismiss(toastId);
      toast.success("Video uploaded!");
    } catch { toast.dismiss(toastId); toast.error("Video upload failed"); }
  };

  const removeVideo = () => setProductVideo(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (["sku","category","subCategory","height","weight","brand","price",
         "previous_price","stock","stock_status","gstPercentage","shippingCharge","rating"].includes(name)) {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if (name === "isBestProduct") {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: { ...prev[name], [activeLanguage]: value } }));
    }
  };

  const handleFeatureInputChange = (e) => {
    setFeaturesInput({ ...featuresInput, [activeLanguage]: e.target.value });
  };

  const addFeature = () => {
    const englishText = featuresInput.en.trim();
    if (!englishText) return;
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, {
        en: englishText,
        hi: featuresInput.hi.trim() || englishText,
        es: featuresInput.es.trim() || englishText,
        zh: featuresInput.zh.trim() || englishText,
      }],
    }));
    setFeaturesInput({ en: "", hi: "", es: "", zh: "" });
  };

  const removeFeature = (index) =>
    setFormData(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));

  // ── Submit ───────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mainImage) { toast.error("Main image is required!"); return; }
    if (!formData.name.en || !formData.description.en) {
      toast.error("Product name and description in English are required!");
      return;
    }

    setLoading(true);
    try {
      const productData = {
        sku: formData.sku,
        category: formData.category,
        subCategory: formData.subCategory,
        height: formData.height,
        weight: formData.weight,
        brand: formData.brand,
        price: Number(formData.price),
        previous_price: Number(formData.previous_price),
        stock: Number(formData.stock),
        stock_status: Number(formData.stock) > 0 ? "active" : "out of stock",
        gstPercentage: Number(formData.gstPercentage),
        shippingCharge: Number(formData.shippingCharge),
        isBestProduct: formData.isBestProduct,
        ratings: Number(formData.rating),
        images: [mainImage, ...subImages],
        uploadedBy: "supplier",
        supplier: user._id,

        // ── FIX: send video correctly ────────────────────────────────
        // If video exists  → send the URL + mark is_special_product true
        // If video removed → send null  + mark is_special_product false
        // This matches what the fixed controller now expects.
        productVideo: productVideo || null,
        is_special_product: !!productVideo,

        // Multilingual fields
        name:            { en: formData.name.en, hi: formData.name.hi || formData.name.en, es: formData.name.es || formData.name.en, zh: formData.name.zh || formData.name.en },
        description:     { en: formData.description.en, hi: formData.description.hi || formData.description.en, es: formData.description.es || formData.description.en, zh: formData.description.zh || formData.description.en },
        material:        { en: formData.material.en, hi: formData.material.hi || formData.material.en, es: formData.material.es || formData.material.en, zh: formData.material.zh || formData.material.en },
        finishType:      { en: formData.finishType.en, hi: formData.finishType.hi || formData.finishType.en, es: formData.finishType.es || formData.finishType.en, zh: formData.finishType.zh || formData.finishType.en },
        origin:          { en: formData.origin.en, hi: formData.origin.hi || formData.origin.en, es: formData.origin.es || formData.origin.en, zh: formData.origin.zh || formData.origin.en },
        careInstruction: { en: formData.careInstruction.en, hi: formData.careInstruction.hi || formData.careInstruction.en, es: formData.careInstruction.es || formData.careInstruction.en, zh: formData.careInstruction.zh || formData.careInstruction.en },
        return_policy:   { en: formData.return_policy.en, hi: formData.return_policy.hi || formData.return_policy.en, es: formData.return_policy.es || formData.return_policy.en, zh: formData.return_policy.zh || formData.return_policy.en },
        measurement:     { en: formData.measurement.en, hi: formData.measurement.hi || formData.measurement.en, es: formData.measurement.es || formData.measurement.en, zh: formData.measurement.zh || formData.measurement.en },
        productType:     { en: formData.productType.en, hi: formData.productType.hi || formData.productType.en, es: formData.productType.es || formData.productType.en, zh: formData.productType.zh || formData.productType.en },
        features: formData.features.map(f => ({ en: f.en, hi: f.hi || f.en, es: f.es || f.en, zh: f.zh || f.en })),
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/products/update-product/${id}`,
        productData
      );

      if (response.data?.message === "Product updated successfully") {
        Swal.fire({ title: "Success!", text: "Product updated successfully.", icon: "success", confirmButtonColor: "#0e459e" });
        navigate("/seller/products");
      } else {
        throw new Error(response.data?.error || "Update failed");
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.message || error.message || "Error updating product");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500 animate-pulse">Loading product data...</p>
      </div>
    );
  }

  const langLabel = activeLanguage === "en" ? "English" : activeLanguage === "hi" ? "Hindi" : activeLanguage === "es" ? "Spanish" : "Chinese";

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col lg:flex-row overflow-hidden">
      {/* LEFT: Image & Video */}
      <div className="w-full lg:w-1/3 bg-white shadow-md p-6 overflow-y-auto lg:sticky lg:top-0 lg:h-screen">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate("/seller/products")} className="p-2 hover:bg-gray-200 rounded-lg transition">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-philosopher text-gray-700">Edit Product</h1>
        </div>

        {/* Main Image */}
        <h2 className="text-lg font-philosopher mb-2">Main Image</h2>
        <label htmlFor="mainImageUpload" className="border-[#2275fc] overflow-hidden relative flex-1 py-10 px-3 sm:px-5 rounded-xl flex items-center justify-center border-2 flex-col cursor-pointer border-dashed text-center hover:bg-blue-50 transition">
          <Upload className="text-[#2275fc] w-10 h-10 z-10" />
          <p className="z-10 text-xs sm:text-sm">{mainImage ? "Change main image" : "Drop your image here or click to browse"}</p>
          {mainImage && <img src={mainImage} alt="Main Preview" className="absolute top-0 left-0 w-full h-full object-cover opacity-40" />}
        </label>
        <input type="file" id="mainImageUpload" accept="image/*" className="hidden" onChange={handleMainImageUpload} />
        {mainImage && (
          <div className="relative mt-3">
            <img src={mainImage} alt="Main" className="w-full h-48 object-cover rounded-lg border" />
            <button type="button" onClick={() => setMainImage(null)} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 shadow"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Sub Images */}
        <h2 className="text-lg font-philosopher mt-6 mb-2">Additional Images</h2>
        <label htmlFor="subImageUpload" className="border-[#2275fc] overflow-hidden relative flex-1 py-10 px-3 sm:px-5 rounded-xl flex items-center justify-center border-2 flex-col cursor-pointer border-dashed text-center hover:bg-blue-50 transition">
          <Plus className="text-[#2275fc] w-8 h-8 z-10" />
          <p className="z-10 text-xs sm:text-sm">Add more images or click to browse</p>
        </label>
        <input type="file" id="subImageUpload" multiple accept="image/*" className="hidden" onChange={handleSubImageUpload} />
        {subImages.length > 0 && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            {subImages.map((img, i) => (
              <div key={i} className="relative">
                <img src={img} alt={`Sub ${i + 1}`} className="w-full h-24 object-cover rounded-lg border" />
                <button type="button" onClick={() => removeSubImage(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}

        {/* ── Video Upload ─────────────────────────────────────────── */}
        <h2 className="text-lg font-philosopher mt-6 mb-2">Product Video (Optional)</h2>
        <label htmlFor="videoUpload" className="border-[#2275fc] overflow-hidden relative flex-1 py-10 px-3 sm:px-5 rounded-xl flex items-center justify-center border-2 flex-col cursor-pointer border-dashed text-center hover:bg-blue-50 transition">
          <Video className="text-[#2275fc] w-10 h-10 z-10" />
          <p className="z-10 text-xs sm:text-sm">
            {productVideo ? "Change video" : "Upload product video (MP4, WebM)"}
          </p>
        </label>
        <input type="file" id="videoUpload" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={handleVideoUpload} />
        {productVideo && (
          <div className="relative mt-3">
            <video src={productVideo} controls className="w-full h-40 rounded-lg border" />
            <button type="button" onClick={removeVideo} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 shadow"><X className="w-4 h-4" /></button>
          </div>
        )}
        {!productVideo && (
          <p className="text-xs text-gray-400 mt-1">Max 50MB. MP4 / WebM supported.</p>
        )}
      </div>

      {/* RIGHT: Form */}
      <div className="w-full lg:w-2/3 overflow-y-auto h-auto lg:h-screen p-6 space-y-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Language Tabs */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-lg font-philosopher mb-4">Language</h2>
            <LanguageTabs />
            <p className="text-sm text-gray-600 mt-2">
              Currently editing: <span className="font-semibold">{langLabel}</span>
              <span className="text-red-500 ml-2">*</span> English is required
            </p>
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-philosopher mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 ml-2">({langLabel})</span>
                </label>
                <input type="text" name="name" value={formData.name[activeLanguage] || ""} onChange={handleInputChange}
                  placeholder="Enter product name" className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required={activeLanguage === "en"} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">SKU <span className="text-red-500">*</span></label>
                <input type="text" name="sku" value={formData.sku} onChange={handleInputChange} className="w-full px-4 py-2 border rounded-lg" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category <span className="text-red-500">*</span></label>
                <select name="category" value={formData.category} onChange={handleInputChange} className="w-full px-4 py-2 border rounded-lg" required>
                  <option value="">Select Category</option>
                  {categories.map(cat => <option key={cat._id} value={cat._id}>{getEnglishText(cat.name)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sub Category <span className="text-red-500">*</span></label>
                <select name="subCategory" value={formData.subCategory} onChange={handleInputChange}
                  disabled={!formData.category || subCategories.length === 0} className="w-full px-4 py-2 border rounded-lg" required>
                  <option value="">{!formData.category ? "Select category first" : subCategories.length === 0 ? "No subcategories" : "Select Sub Category"}</option>
                  {subCategories.map(sub => <option key={sub._id} value={sub._id}>{getEnglishText(sub.name)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Material <span className="text-xs text-gray-500 ml-1">({langLabel})</span></label>
                <input type="text" name="material" value={formData.material[activeLanguage] || ""} onChange={handleInputChange} placeholder="e.g., Brass" className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                <input type="text" name="brand" value={formData.brand} onChange={handleInputChange} className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rating (0-5)</label>
                <input type="number" name="rating" value={formData.rating} onChange={handleInputChange} min="0" max="5" step="0.1" className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stock Quantity <span className="text-red-500">*</span></label>
                <input type="number" name="stock" value={formData.stock} onChange={handleInputChange} min="0" className="w-full px-4 py-2 border rounded-lg" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stock Status</label>
                <select name="stock_status" value={formData.stock_status} onChange={handleInputChange} className="w-full px-4 py-2 border rounded-lg" disabled>
                  <option value="active">Active (In Stock)</option>
                  <option value="inactive">Inactive</option>
                  <option value="out of stock">Out of Stock</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">Status updates automatically based on stock quantity.</p>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-philosopher mb-4">Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Base Price (MRP) <span className="text-red-500">*</span></label>
                <div className="relative"><span className="absolute left-3 top-2.5">₹</span><input type="number" name="previous_price" value={formData.previous_price} onChange={handleInputChange} className="w-full pl-8 pr-4 py-2 border rounded-lg" required /></div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Selling Price <span className="text-red-500">*</span></label>
                <div className="relative"><span className="absolute left-3 top-2.5">₹</span><input type="number" name="price" value={formData.price} onChange={handleInputChange} className="w-full pl-8 pr-4 py-2 border rounded-lg" required /></div>
              </div>
              {formData.previous_price && formData.price && (
                <div className="md:col-span-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800"><strong>Discount: </strong>{(((formData.previous_price - formData.price) / formData.previous_price) * 100).toFixed(1)}% off — Save ₹{(formData.previous_price - formData.price).toFixed(2)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Specifications */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-philosopher mb-4">Specifications</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Height</label><input type="text" name="height" value={formData.height} onChange={handleInputChange} className="w-full px-4 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Weight</label><input type="text" name="weight" value={formData.weight} onChange={handleInputChange} className="w-full px-4 py-2 border rounded-lg" /></div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Finish Type <span className="text-xs text-gray-500 ml-1">({langLabel})</span></label>
                <input type="text" name="finishType" value={formData.finishType[activeLanguage] || ""} onChange={handleInputChange} className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Care Instructions <span className="text-xs text-gray-500 ml-1">({langLabel})</span></label>
                <input type="text" name="careInstruction" value={formData.careInstruction[activeLanguage] || ""} onChange={handleInputChange} className="w-full px-4 py-2 border rounded-lg" />
              </div>
            </div>
          </div>

          {/* Product Type */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-philosopher mb-4">Product Type</h2>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type <span className="text-xs text-gray-500 ml-1">({langLabel})</span></label>
            <input type="text" name="productType" value={formData.productType[activeLanguage] || ""} onChange={handleInputChange} placeholder="e.g., Religious Statue" className="w-full px-4 py-2 border rounded-lg" />
          </div>

          {/* Features */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-lg font-philosopher mb-4">Features</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Add Feature ({langLabel})</label>
                <div className="flex gap-2">
                  <input type="text" value={featuresInput[activeLanguage] || ""} onChange={handleFeatureInputChange} className="flex-1 px-4 py-2 border rounded-lg" />
                  <button type="button" onClick={addFeature} disabled={!featuresInput.en.trim()} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50">+</button>
                </div>
                <p className="text-xs text-gray-500 mt-1">English is required. Others auto-fill from English.</p>
              </div>
              <div className="space-y-2">
                {formData.features.map((feat, idx) => (
                  <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between items-start"><span className="text-sm font-medium">Feature {idx + 1}</span><button type="button" onClick={() => removeFeature(idx)} className="text-red-500"><X className="w-4 h-4" /></button></div>
                    <div className="text-sm"><span className="font-medium">EN:</span> {feat.en}</div>
                    <div className="text-sm"><span className="font-medium">HI:</span> {feat.hi}</div>
                    <div className="text-sm"><span className="font-medium">ES:</span> {feat.es}</div>
                    <div className="text-sm"><span className="font-medium">ZH:</span> {feat.zh}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-philosopher mb-4">Description</h2>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product Description <span className="text-red-500">*</span> <span className="text-xs text-gray-500 ml-1">({langLabel})</span></label>
            <textarea name="description" value={formData.description[activeLanguage] || ""} onChange={handleInputChange} rows="6" className="w-full px-4 py-2 border rounded-lg" required={activeLanguage === "en"} />
          </div>

          {/* Additional Settings */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-philosopher mb-4">Additional Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Measurement <span className="text-xs text-gray-500 ml-1">({langLabel})</span></label>
                <input type="text" name="measurement" value={formData.measurement[activeLanguage] || ""} onChange={handleInputChange} className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Shipping Charge (₹)</label><input type="number" name="shippingCharge" value={formData.shippingCharge} onChange={handleInputChange} className="w-full px-4 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">GST Percentage (%)</label><input type="number" name="gstPercentage" value={formData.gstPercentage} onChange={handleInputChange} className="w-full px-4 py-2 border rounded-lg" /></div>
              <div className="flex items-center gap-2">
                <input type="checkbox" name="isBestProduct" checked={formData.isBestProduct} onChange={handleInputChange} className="w-4 h-4" />
                <label className="text-sm font-medium text-gray-700">Mark as Best Product</label>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4 pb-6">
            <button type="submit" disabled={loading}
              className="flex-1 bg-[#0e459e] text-white px-4 py-3 rounded-lg hover:bg-[#033485] transition flex items-center justify-center gap-2 font-medium disabled:opacity-50">
              <Save className="w-5 h-5" /> {loading ? "Updating..." : "Update Product"}
            </button>
            <button type="button" onClick={() => navigate("/seller/products")}
              className="px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplierEditProduct;